
import React, { useState, useMemo, useEffect } from 'react';
import { Plus, TrendingUp, ArrowUpRight, Wallet, Download, LineChart, FileText, CheckCircle2, AlertCircle, Clock, Loader2 } from 'lucide-react';
import { Company } from './types';
import { Card, Badge } from './SharedComponents';
import { db } from './supabase';

export const GlobalFinance = ({ companies, onNewTransaction }: { companies: Company[], onNewTransaction: () => void }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  
  const mrrTotal = useMemo(() => companies.reduce((acc, c) => acc + (c.mrr || 0), 0), [companies]);
  const totalReceivable = useMemo(() => companies.reduce((acc, c) => acc + (c.receivable || 0), 0), [companies]);
  const totalPayable = useMemo(() => companies.reduce((acc, c) => acc + (c.payable || 0), 0), [companies]);

  useEffect(() => {
    const loadFinance = async () => {
      try {
        const data = await (db as any).getSaasTransactions();
        setTransactions(data);
      } catch (err: any) {
        console.error("Erro ao carregar financeiro:", err?.message || "Erro desconhecido na API");
      }
    };
    loadFinance();
  }, []);

  const handleExportCSV = () => {
    setIsExporting(true);
    
    const rows = [
      ["sep=;"],
      ["RELATORIO FINANCEIRO SAAS - EVENTLOG CORE"],
      ["Data de Geracao", new Date().toLocaleString('pt-BR')],
      [""],
      ["LISTAGEM DE TRANSACOES"],
      ["Empresa", "Valor (R$)", "Status", "Plano", "Vencimento", "Descricao"]
    ];

    transactions.forEach(tx => {
      rows.push([
        tx.company_name,
        tx.amount.toFixed(2).replace('.', ','),
        tx.status,
        tx.plan,
        new Date(tx.due_date).toLocaleDateString('pt-BR'),
        tx.description || ""
      ]);
    });

    const csvString = rows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";")
    ).join("\r\n");

    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const csvContent = new TextEncoder().encode(csvString);
    const blob = new Blob([BOM, csvContent], { type: 'text/csv;charset=utf-8' });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `EventLog_Financeiro_SaaS_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    
    setTimeout(() => {
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsExporting(false);
    }, 800);
  };

  return (
    <div className="p-10 space-y-8 animate-in fade-in duration-500 bg-[#fcfdfe] min-h-screen text-secondary">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-tighter text-secondary">Gestão Financeira SaaS</h1>
          <p className="text-primary font-black uppercase text-[10px] tracking-[0.4em]">Performance de Receita e Fluxo Master</p>
        </div>
        <div className="flex gap-3">
          <button 
            disabled={isExporting}
            onClick={handleExportCSV} 
            className="bg-white border border-gray-100 text-secondary px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-2 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            {isExporting ? 'Processando...' : 'CSV Financeiro'}
          </button>
          <button 
            onClick={onNewTransaction} 
            className="bg-primary text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:brightness-110 shadow-xl shadow-primary/20 active:scale-95 transition-all"
          >
            <Plus size={18} /> Novo Título
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <FinanceStat label="MRR Plataforma" value={mrrTotal} icon={TrendingUp} color="text-primary" />
        <FinanceStat label="Contas a Receber" value={totalReceivable} icon={ArrowUpRight} color="text-green-600" />
        <FinanceStat label="Inadimplência Real" value={transactions.filter(t => t.status === 'PAST_DUE').reduce((acc, t) => acc + t.amount, 0)} icon={AlertCircle} color="text-red-500" />
        <FinanceStat label="Saldo Consolidado" value={totalReceivable - totalPayable} icon={Wallet} color="text-secondary" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="bg-white border border-gray-100 p-8 flex flex-col justify-between h-[450px] shadow-xl rounded-[48px]">
           <div className="flex justify-between items-start mb-6">
              <div>
                 <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Crescimento SaaS</h3>
                 <p className="text-xl font-black text-secondary uppercase tracking-tight">Evolução Mensal MRR</p>
              </div>
              <div className="p-2 bg-primary/10 rounded-xl text-primary">
                 <LineChart size={20} />
              </div>
           </div>
           
           <div className="flex-1 flex items-end gap-3 px-4 mt-12 mb-4">
              {[30, 45, 35, 60, 80, 55, 90, 100].map((h, i) => (
                <div 
                  key={i} 
                  className="flex-1 bg-gradient-to-t from-primary/5 via-primary/20 to-primary/40 rounded-t-2xl group relative transition-all duration-500 hover:to-primary hover:scale-x-105 cursor-pointer shadow-inner" 
                  style={{ height: `${h}%` }}
                >
                   {/* Tooltip corrigido para visibilidade total */}
                   <div className="absolute -top-12 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-20 scale-90 group-hover:scale-100">
                      <div className="bg-secondary text-white px-3 py-2 rounded-xl text-[10px] font-black whitespace-nowrap shadow-2xl border border-white/10 flex flex-col items-center">
                         <span className="text-primary tracking-widest uppercase text-[7px] mb-0.5">Faturamento</span>
                         R$ {(h * 150).toLocaleString('pt-BR')}
                         <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-secondary rotate-45 border-r border-b border-white/10"></div>
                      </div>
                   </div>
                   
                   {/* Label do Mês */}
                   <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[8px] font-black text-gray-300 uppercase tracking-widest">
                      M{i+1}
                   </div>
                </div>
              ))}
           </div>
           <div className="pt-10 border-t border-gray-50 flex justify-between items-center opacity-50">
              <span className="text-[9px] font-black uppercase tracking-widest">Jan 2025</span>
              <span className="text-[9px] font-black uppercase tracking-widest">Ago 2025</span>
           </div>
        </Card>

        <Card className="lg:col-span-2 bg-white border border-gray-100 p-8 shadow-xl rounded-[48px]">
           <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-3 text-secondary">
                 <FileText size={24} className="text-primary" /> Histórico de Faturamento Real
              </h3>
              <Badge className="bg-green-50 text-green-600 border-none font-black text-[9px] tracking-widest">
                 CORE SYNC: ONLINE
              </Badge>
           </div>
           <div className="space-y-4 max-h-[400px] overflow-y-auto pr-4 scrollbar-hide">
              {transactions.length > 0 ? transactions.map(tx => (
                <div key={tx.id} className="flex items-center justify-between p-6 bg-gray-50/50 rounded-3xl border border-gray-100 hover:border-primary/20 hover:bg-white hover:shadow-xl transition-all group">
                   <div className="flex items-center gap-5">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black shadow-inner transition-transform group-hover:scale-110 ${
                        tx.status === 'PAID' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                         {tx.status === 'PAID' ? <CheckCircle2 size={24} /> : <Clock size={24} />}
                      </div>
                      <div>
                         <p className="font-black text-secondary uppercase text-sm tracking-tight">{tx.company_name || 'Lançamento Manual'}</p>
                         <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-0.5">
                           Plano {tx.plan} • <span className="text-primary">Vence: {new Date(tx.due_date).toLocaleDateString()}</span>
                         </p>
                      </div>
                   </div>
                   <div className="text-right">
                      <p className="font-black text-secondary text-xl tracking-tighter">R$ {tx.amount.toLocaleString('pt-BR')}</p>
                      <Badge className={`text-[8px] font-black border-none mt-1 ${
                        tx.status === 'PAID' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                      }`}>{tx.status === 'PAID' ? 'LIQUIDADO' : 'PENDENTE'}</Badge>
                   </div>
                </div>
              )) : (
                <div className="py-24 text-center text-gray-200 font-black uppercase tracking-[0.4em] italic text-xs">
                   Sem movimentações registradas
                </div>
              )}
           </div>
        </Card>
      </div>
    </div>
  );
};

const FinanceStat = ({ label, value, icon: Icon, color }: any) => (
  <Card className="bg-white border border-gray-100 p-8 flex flex-col gap-6 group hover:shadow-2xl transition-all rounded-[40px] cursor-default">
    <div className={`w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center transition-transform group-hover:scale-110 shadow-inner ${color}`}>
       <Icon size={28} />
    </div>
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1">{label}</p>
      <p className={`text-3xl font-black tracking-tighter ${color}`}>R$ {value.toLocaleString('pt-BR')}</p>
    </div>
  </Card>
);
